import { ReactNode, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { ErrorPanel, LogViewer, Progress } from '@backstage/core-components';
import { useTaskEventStream } from '@backstage/plugin-scaffolder-react';
import { DefaultTemplateOutputs, TaskSteps } from '@backstage/plugin-scaffolder-react/alpha';

export type AdHocJobTaskProgressProps = {
  taskId: string;
};

export function AdHocJobTaskProgress({ taskId }: AdHocJobTaskProgressProps): ReactNode {
  const taskStream = useTaskEventStream(taskId);

  const steps = useMemo(
    () => taskStream.task?.spec.steps.map(step => ({ ...step, ...taskStream.steps[step.id] })) ?? [],
    [taskStream],
  );

  const activeStep = useMemo(() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].status !== 'open') {
        return i;
      }
    }
    return 0;
  }, [steps]);

  if (!taskStream.task) {
    return <Progress />;
  }

  return (
    <Box display="flex" flexDirection="column">
      {taskStream.error && (
        <Box paddingBottom={2}>
          <ErrorPanel error={taskStream.error} title={taskStream.error.message} />
        </Box>
      )}
      <Box paddingBottom={2}>
        <TaskSteps isComplete={taskStream.completed} steps={steps} activeStep={activeStep} />
      </Box>
      {taskStream.output && <DefaultTemplateOutputs output={taskStream.output} />}
      <LogViewer
        text={Object.values(taskStream.stepLogs)
          .map(lines => lines.join('\n'))
          .filter(Boolean)
          .join('\n')}
      />
    </Box>
  );
}
